import { useQuery } from "@tanstack/react-query";
import { ustIdNorm, viesZiel } from "@talentkontor/domain";
import { api, type Api, type VatCheckRow } from "../data";
import { t } from "../lib/sprache";
import { viesErgebnisText, viesNichtAnwendbar, viesStatusText, idWortVon } from "../model/vies";
import { PillView, useAction } from "./ui";
import { toast } from "./Toast";

/** Letzte VIES-Prüfung einer Nummer in dieser Agentur (public.vat_checks, 0445) */
export function useLetztePruefung(orgId: string, vatId: string | null | undefined) {
  const norm = ustIdNorm(vatId);
  const eu = viesZiel(vatId).art === "eu";
  return useQuery({
    queryKey: ["vat_checks", orgId, norm],
    enabled: eu,
    queryFn: async () => (await api.select<VatCheckRow>("vat_checks", {
      columns: "id, vat_id, country_code, status, checked_at, request_identifier, name, address, error_code",
      filters: [["org_id", "eq", orgId], ["vat_id", "eq", norm]], order: [["checked_at", "desc"]], limit: 1,
    }).catch(() => []))[0] ?? null,
  });
}

/**
 * Eine Nummer bei VIES prüfen und das Ergebnis als eigene Meldung zeigen (Marke speichern, Knopf „Bei VIES prüfen“). Die Meldung
 * kommt immer, auch aus dem Zwischenspeicher, zusätzlich zu „Marke gespeichert.“ (Abnahme V-2: das Ergebnis stand sonst nur in der
 * Ansicht). Nummern ohne Abfrage (Schweiz, Großbritannien, Drittland) melden den Grund. Ohne Serverfunktion (Demo) keine Meldung.
 */
export async function ustIdPruefenMitMeldung(orgId: string, vatId: string, brandId?: string | null, a: Pick<Api, "vatCheck"> = api): Promise<void> {
  if (!a.vatCheck || viesZiel(vatId).art === "leer") return;
  if (viesZiel(vatId).art !== "eu") {
    toast(viesNichtAnwendbar(vatId)!);
    return;
  }
  try {
    const m = viesErgebnisText(await a.vatCheck({ org: orgId, vat_id: vatId, ...(brandId ? { brand: brandId } : {}) }));
    // Prüfergebnis, kein Fehler bei uns: bleibt stehen, aber ohne „Melden“
    toast(m.text, m.alert ? "alert" : undefined, undefined, { melden: false });
  } catch (e) {
    toast(t(`Die ${idWortVon(vatId)} ließ sich gerade nicht bei VIES prüfen (${(e as Error).message}). Sie gilt vorerst als nicht geprüft.`, `The VAT ID could not be checked with VIES just now (${(e as Error).message}). It counts as not checked for now.`), "alert", undefined, { melden: false });
  }
}

/**
 * Stand der Prüfung bei VIES mit Pille und Knopf (Marke, Generator). Ohne Serverfunktion (Demo) nur der Stand.
 * Steuerprüfung L6: ungültig fragt beim Festschreiben nach, „nicht erreichbar“ sperrt nie.
 */
export function UstIdPruefung({ orgId, vatId, brandId, id }: { orgId: string; vatId: string | null | undefined; brandId?: string | null; id?: string }) {
  const q = useLetztePruefung(orgId, vatId);
  const { busy, run } = useAction();
  const nicht = viesNichtAnwendbar(vatId);
  if (nicht) return <small id={id} className="muted">{nicht}</small>;
  if (q.isPending) return <small id={id} className="muted">{t("Prüfung bei VIES wird geladen …", "Loading VIES check …")}</small>;
  const s = viesStatusText(q.data ?? null);
  return (
    <span id={id} className="stack">
      <span className="row">
        <PillView pill={{ label: t(`VIES: ${s.kurz}`, `VIES: ${s.kurz}`), tone: s.ton, symbol: s.ton === "success" ? "✓" : s.ton === "error" ? "!" : s.ton === "warning" ? "◷" : "○" }} />
        {api.vatCheck && (
          <button type="button" className="quiet" disabled={busy === "vies"} aria-busy={busy === "vies"}
            onClick={() => void run("vies", async () => { await ustIdPruefenMitMeldung(orgId, vatId!, brandId); await q.refetch(); })}>
            {busy === "vies" ? t("Wird geprüft …", "Checking …") : t("Bei VIES prüfen", "Check with VIES")}
          </button>
        )}
      </span>
      <small>{s.text}</small>
    </span>
  );
}
